import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { firstValueFrom } from 'rxjs';
import { InventoryService } from '../../../_services/inventory.service';
import { ImportPlan, ImportUpdate } from '../inventory-import';

export interface ImportPreviewData {
  plan: ImportPlan;
  fileName: string;
}

export interface ImportPreviewResult {
  saved: number;
  failed: number;
}

// Shows what an imported Inventory sheet would change, and applies it only
// when the person confirms - one item at a time through the normal save, so
// every change is checked and stock changes land in each item's history.
@Component({
    selector: 'app-import-preview',
    imports: [CommonModule, MatIconModule],
    templateUrl: './import-preview.component.html'
})
export class ImportPreviewComponent {
  readonly data = inject<ImportPreviewData>(MAT_DIALOG_DATA);
  private readonly ref = inject(MatDialogRef<ImportPreviewComponent, ImportPreviewResult>);
  private readonly inventoryService = inject(InventoryService);

  applying = false;
  done = false;
  progress = 0;
  saved = 0;
  failures: { update: ImportUpdate; message: string }[] = [];
  showAllProblems = false;

  get plan(): ImportPlan {
    return this.data.plan;
  }

  get changeCount(): number {
    return this.plan.updates.reduce((n, u) => n + u.changes.length, 0);
  }

  get problemsShown() {
    return this.showAllProblems ? this.plan.problems : this.plan.problems.slice(0, 8);
  }

  fmt(v: number | null, money: boolean): string {
    if (v === null) return money ? 'none' : '0';
    return money ? `£${v.toFixed(2)}` : String(v);
  }

  async apply(): Promise<void> {
    if (this.applying || !this.plan.updates.length) return;
    this.applying = true;
    this.ref.disableClose = true;
    const reason = `Imported from spreadsheet ${this.data.fileName}`.slice(0, 180);
    for (const u of this.plan.updates) {
      try {
        await firstValueFrom(this.inventoryService.updateVariant(u.id, { ...u.update, reason }));
        this.saved++;
      } catch (e: any) {
        this.failures.push({ update: u, message: e?.error?.message || 'Could not save' });
      }
      this.progress++;
    }
    this.applying = false;
    this.done = true;
    this.ref.disableClose = false;
  }

  close(): void {
    this.ref.close(this.done ? { saved: this.saved, failed: this.failures.length } : undefined);
  }
}
